import React, { useState, useEffect, useRef } from 'react'

interface LogEntry {
  timestamp: string,
  type: 'log' | 'warn' | 'error' | 'info',
  args: string,
  id: string,
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    height: '40vh',
    backgroundColor: 'rgba(0, 0, 0, 0.92)',
    color: '#fff',
    zIndex: 999999,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'monospace',
    fontSize: '11px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 10px',
    backgroundColor: '#333',
    borderBottom: '1px solid #555',
    flexShrink: 0,
  },
  title: {
    fontWeight: 'bold',
    fontSize: '12px',
  },
  closeBtn: {
    background: '#e53935',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '4px 10px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  clearBtn: {
    background: '#555',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '4px 10px',
    fontSize: '12px',
    cursor: 'pointer',
    marginRight: '8px',
  },
  logList: {
    flex: 1,
    overflowY: 'auto',
    padding: '6px 10px',
    WebkitOverflowScrolling: 'touch',
  },
  logEntry: {
    padding: '3px 0',
    borderBottom: '1px solid #333',
    wordBreak: 'break-all',
    whiteSpace: 'pre-wrap',
  },
  error: { color: '#ff5252' },
  warn: { color: '#ffc107' },
  info: { color: '#64b5f6' },
  log: { color: '#ccc' },
  badge: {
    position: 'fixed',
    bottom: '10px',
    right: '10px',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#e53935',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
    zIndex: 999999,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
  },
}

const serialize = (args: any[]): string => args.map((arg) => {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}\n${arg.stack}`
  if (typeof arg === 'object') {
    try {
      return JSON.stringify(arg, null, 2)
    } catch {
      return String(arg)
    }
  }
  return String(arg)
}).join(' ')

let idCounter = 0

const DebugConsole: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [visible, setVisible] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const originalConsole = {
      // eslint-disable-next-line no-console
      log: console.log,
      // eslint-disable-next-line no-console
      warn: console.warn,
      // eslint-disable-next-line no-console
      error: console.error,
      // eslint-disable-next-line no-console
      info: console.info,
    }

    const intercept = (type: LogEntry['type']) => (...args: any[]) => {
      originalConsole[type](...args)
      const entry: LogEntry = {
        timestamp: new Date().toLocaleTimeString(),
        type,
        args: serialize(args),
        id: `${Date.now()}-${idCounter += 1}`,
      }
      setLogs((prev) => [...prev.slice(-500), entry])
    }

    // eslint-disable-next-line no-console
    console.log = intercept('log')
    // eslint-disable-next-line no-console
    console.warn = intercept('warn')
    // eslint-disable-next-line no-console
    console.error = intercept('error')
    // eslint-disable-next-line no-console
    console.info = intercept('info')

    // Catch unhandled errors
    const errorHandler = (event: ErrorEvent) => {
      const entry: LogEntry = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'error',
        args: `[Uncaught] ${event.message}\n  at ${event.filename}:${event.lineno}:${event.colno}`,
        id: `${Date.now()}-${idCounter += 1}`,
      }
      setLogs((prev) => [...prev.slice(-500), entry])
    }

    // Catch unhandled promise rejections
    const rejectionHandler = (event: PromiseRejectionEvent) => {
      const reason = event.reason instanceof Error
        ? `${event.reason.message}\n${event.reason.stack}`
        : String(event.reason)
      const entry: LogEntry = {
        timestamp: new Date().toLocaleTimeString(),
        type: 'error',
        args: `[Unhandled Rejection] ${reason}`,
        id: `${Date.now()}-${idCounter += 1}`,
      }
      setLogs((prev) => [...prev.slice(-500), entry])
    }

    window.addEventListener('error', errorHandler)
    window.addEventListener('unhandledrejection', rejectionHandler)

    return () => {
      // eslint-disable-next-line no-console
      console.log = originalConsole.log
      // eslint-disable-next-line no-console
      console.warn = originalConsole.warn
      // eslint-disable-next-line no-console
      console.error = originalConsole.error
      // eslint-disable-next-line no-console
      console.info = originalConsole.info
      window.removeEventListener('error', errorHandler)
      window.removeEventListener('unhandledrejection', rejectionHandler)
    }
  }, [])

  useEffect(() => {
    if (visible && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [logs, visible])

  const errorCount = logs.filter((l) => l.type === 'error').length

  const handleOpen = () => setVisible(true)
  const handleKeyOpen = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') setVisible(true)
  }

  if (!visible) {
    return (
      <div
        style={styles.badge}
        onClick={handleOpen}
        onKeyDown={handleKeyOpen}
        role="button"
        tabIndex={0}
      >
        {errorCount > 0 ? errorCount : '🔍'}
      </div>
    )
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.header}>
        <span style={styles.title}>
          Debug Console (
          {logs.length}
          {' entries)'}
        </span>
        <div>
          <button type="button" style={styles.clearBtn} onClick={() => setLogs([])}>Clear</button>
          <button type="button" style={styles.closeBtn} onClick={() => setVisible(false)}>✕</button>
        </div>
      </div>
      <div style={styles.logList} ref={listRef}>
        {logs.map((entry) => (
          <div key={entry.id} style={{ ...styles.logEntry, ...styles[entry.type] }}>
            <span style={{ opacity: 0.5 }}>
              [
              {entry.timestamp}
              ]
              {' '}
            </span>
            <span style={{ fontWeight: entry.type === 'error' ? 'bold' : 'normal' }}>
              [
              {entry.type.toUpperCase()}
              ]
            </span>
            {' '}
            {entry.args}
          </div>
        ))}
      </div>
    </div>
  )
}

export default DebugConsole
